import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "qgroup_token";

axios.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);
export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const login = (email, password) =>
  axios.post(`${API}/auth/login`, { email, password }).then((r) => r.data);
export const verifyOtp = (pending_token, code) =>
  axios.post(`${API}/auth/verify-otp`, { pending_token, code }).then((r) => r.data);
export const resendOtp = (pending_token) =>
  axios.post(`${API}/auth/resend-otp`, { pending_token }).then((r) => r.data);
export const getMe = () => axios.get(`${API}/auth/me`).then((r) => r.data);
export const getUsers = () => axios.get(`${API}/users`).then((r) => r.data);

// Admin: Manage Users
export const adminListUsers = () => axios.get(`${API}/admin/users`).then((r) => r.data);
export const adminCreateUser = (payload) =>
  axios.post(`${API}/admin/users`, payload).then((r) => r.data);
export const adminUpdateUser = (id, payload) =>
  axios.put(`${API}/admin/users/${id}`, payload).then((r) => r.data);
export const adminDeleteUser = (id) =>
  axios.delete(`${API}/admin/users/${id}`).then((r) => r.data);
export const adminUploadImage = (file) => {
  const form = new FormData();
  form.append("file", file);
  return axios
    .post(`${API}/admin/upload-image`, form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};
export const getCompanyOverrides = () =>
  axios.get(`${API}/companies/overrides`).then((r) => r.data);
export const adminUpdateCompanyLogo = (key, logo) =>
  axios.put(`${API}/admin/companies/${key}`, { logo }).then((r) => r.data);

export const getEnvelope = () => axios.get(`${API}/envelope`).then((r) => r.data);

export const getDocuments = (company) =>
  axios.get(`${API}/documents`, { params: company ? { company } : {} }).then((r) => r.data);
export const addDocument = (payload) =>
  axios.post(`${API}/documents`, payload).then((r) => r.data);
export const deleteDocument = (id) =>
  axios.delete(`${API}/documents/${id}`).then((r) => r.data);

export const sendEnvelope = (payload) =>
  axios.post(`${API}/envelopes/send`, payload).then((r) => r.data);

export const voidEnvelope = (id) =>
  axios.post(`${API}/documents/${id}/void`).then((r) => r.data);
export const moveEnvelope = (id, category) =>
  axios.post(`${API}/documents/${id}/move`, { category }).then((r) => r.data);
export const getEnvelopeHistory = (id) =>
  axios.get(`${API}/documents/${id}/history`).then((r) => r.data);

export const getDocument = (id) =>
  axios.get(`${API}/documents/${id}`).then((r) => r.data);
export const getEnvelopeSummary = (id) =>
  axios.get(`${API}/documents/${id}/envelope`).then((r) => r.data);
export const getSignedPdfArrayBuffer = (id, docIndex) =>
  axios
    .get(`${API}/documents/${id}/signed-pdf`, {
      responseType: "arraybuffer",
      params: docIndex != null ? { doc_index: docIndex } : {},
    })
    .then((r) => r.data);
export const getFileArrayBuffer = (storagePath) =>
  axios.get(`${API}/files/${storagePath}`, { responseType: "arraybuffer" }).then((r) => r.data);
export const signDocument = (id, fieldValues) =>
  axios
    .post(`${API}/documents/${id}/sign`, {
      field_values: fieldValues,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    .then((r) => r.data);
export const declineDocument = (id, reason = "") =>
  axios.post(`${API}/documents/${id}/decline`, { reason }).then((r) => r.data);
export const viewDocument = (id) =>
  axios.post(`${API}/documents/${id}/view`).then((r) => r.data);

const _downloadBlob = async (url, filename) => {
  const res = await axios.get(url, { responseType: "blob" });
  const safe = (filename || "download").replace(/[\\/:*?"<>|]+/g, "_");
  // Use the server's blob (with its own content-type) directly so ZIPs stay ZIPs.
  const objUrl = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = safe;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(objUrl);
  }, 1500);
};

export const downloadSignedPdf = (id, filename, docIndex) =>
  _downloadBlob(
    `${API}/documents/${id}/signed-pdf${docIndex != null ? `?doc_index=${docIndex}` : ""}`,
    filename
  );
export const downloadCertificatePdf = (id, filename) =>
  _downloadBlob(`${API}/documents/${id}/certificate-pdf`, filename);
export const downloadBundle = (id, include, filename) =>
  _downloadBlob(`${API}/documents/${id}/download-bundle?include=${include.join(",")}`, filename);
export const downloadZip = (id, include, filename) =>
  _downloadBlob(`${API}/documents/${id}/download-zip?include=${include.join(",")}`, filename);

export const getActivities = (company) =>
  axios.get(`${API}/activities`, { params: company ? { company } : {} }).then((r) => r.data);
export const addActivity = (payload) =>
  axios.post(`${API}/activities`, payload).then((r) => r.data);
export const deleteActivity = (id) =>
  axios.delete(`${API}/activities/${id}`).then((r) => r.data);

export const getTemplates = (company) =>
  axios.get(`${API}/templates`, { params: company ? { company } : {} }).then((r) => r.data);
export const addTemplate = (payload) =>
  axios.post(`${API}/templates`, payload).then((r) => r.data);
export const deleteTemplate = (id) =>
  axios.delete(`${API}/templates/${id}`).then((r) => r.data);

export const uploadDocument = (file, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  return axios
    .post(`${API}/upload`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    })
    .then((r) => r.data);
};

export const fileUrl = (storagePath) => ({
  url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${storagePath}`,
  httpHeaders: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` },
});
